import { isArray, isPlainObject, mergeWith, omit as omitLodash, pick as pickLodash } from 'lodash';

export const noop = () => undefined;

export const omit = (obj: Record<string, any>, keys?: string[], defaultObject = obj) => {
    return keys ? omitLodash(obj, keys) : defaultObject;
};

export const pick = (obj: Record<string, any>, keys?: string[], defaultObject = obj) => {
    return keys ? pickLodash(obj, keys) : defaultObject;
};

/**
 * Initialize the values of a destination object from the values of a source object
 * @param destination destination object
 * @param source source object
 * @returns the initialize destination object
 */
export const initObj = (destination: Record<string, any>, source: Record<string, any>) => {
    const result = destination;
    Object.keys(destination).forEach(key => {
        if (key in source && source[key] != null) {
            if (isPlainObject(destination[key]) && isPlainObject(source[key])) {
                result[key] = initObj(destination[key], source[key]);
            } else if (destination[key] == null) {
                result[key] = source[key];
            }
        } else {
            result[key] = destination[key];
        }
    });
    return result;
};

export function generateUniqueName(prefix: string, names: string[]): string {
    const nameSet = new Set(names);
    let increment = names.length + 1;
    let uniqueName = `${prefix}${increment}`;

    while (nameSet.has(uniqueName)) {
        increment += 1;
        uniqueName = `${prefix}${increment}`;
    }

    return uniqueName;
}

/**
 * Recursively merges own and inherited enumerable properties of source
 * objects into the destination object, skipping source properties that resolve
 * to `undefined`. Only plain object properties are merged recursively, arrays are ignored.
 * Other objects and value types are overridden by assignment. Source objects
 * are applied from left to right. Subsequent sources overwrite property
 * assignments of previous sources.
 * @param destination The destination object.
 * @param source The source objects.
 * @returns
 */
export function mergeDicts(
    destination: Record<string, any>,
    source: Record<string, any>
): Record<string, any> {
    /**
     * Ignore the merge of arrays and merge only the objects
     */
    function mergeDictCustomizer(objValue: any, srcValue: any) {
        if (isArray(objValue)) {
            return srcValue;
        }
        return undefined;
    }
    return mergeWith(destination, source, mergeDictCustomizer);
}

/**
 * Upserts objects from one array into another based on a common key,
 * updating only the specified properties on matching objects.
 *
 * This function:
 * - Creates a Map from `targetArray` keyed by the specified `key`.
 * - Uses Array's `reduce` to iterate over `sourceArray` without explicit loops.
 * - For each element in `sourceArray`, if an object with the same `key` exists in the Map:
 *   - Updates only the specified properties (`keysToUpdate`).
 * - If no matching object is found, it adds the new object to the Map.
 *
 * The function returns a new array that includes all elements from the original `targetArray` (possibly updated)
 * plus any unique elements from `sourceArray`.
 *
 * @template T - The type of the objects in both arrays.
 * @param {T[]} targetArray - The base array of objects.
 * @param {T[]} sourceArray - The array of objects to be upserted into `targetArray`.
 * @param {keyof T} key - The property name used to identify and match objects between the two arrays.
 * @param {(keyof T)[]} keysToUpdate - The array of property names to update on matched objects.
 * @returns {T[]} A new array containing the upserted results.
 */
export function upsertObjectsByKey<T extends Record<PropertyKey, any>>(
    targetArray: T[],
    sourceArray: T[],
    key: keyof T,
    keysToUpdate?: (keyof T)[]
): T[] {
    // Create a Map from targetArray indexed by the identifying key for O(1) lookups
    const map = new Map<unknown, T>(targetArray.map(item => [item[key], { ...item }]));

    // Use reduce to process sourceArray without explicit loops
    sourceArray.reduce((acc, item) => {
        const itemKey = item[key];

        if (acc.has(itemKey)) {
            if (keysToUpdate?.length) {
                // If the item exists, update the specified properties
                const existingItem = acc.get(itemKey)!;
                keysToUpdate.forEach(k => {
                    if (k in item) {
                        existingItem[k] = item[k];
                    }
                });
                acc.set(itemKey, existingItem);
            }
        } else {
            // If the item does not exist, add it
            acc.set(itemKey, { ...item });
        }
        return acc;
    }, map);

    // Return a new array composed of all upserted items from the Map
    return Array.from(map.values());
}
